import { Exam, DayPlan, StudyPlan, SubjectPlan, DIFFICULTY_MINUTES } from '../types/index'
import { addDays, isSameDay, isAfter, isBefore, startOfDay } from 'date-fns'

function getMinutesPerPage(exam: Exam): number {
  if (exam.difficulty === 'custom' && exam.customMinutesPerPage) {
    return exam.customMinutesPerPage
  }
  return DIFFICULTY_MINUTES[exam.difficulty]
}

function isRestDay(date: Date, weekDays: number[], specificDates: Date[]): boolean {
  const dayOfWeek = date.getDay()
  if (weekDays.includes(dayOfWeek)) return true
  return specificDates.some(d => isSameDay(d, date))
}

function getStudyDays(start: Date, end: Date, weekDayRests: number[], specificRests: Date[]): Date[] {
  const days: Date[] = []
  let current = startOfDay(start)
  const endDay = startOfDay(end)
  while (isBefore(current, endDay)) {
    if (!isRestDay(current, weekDayRests, specificRests)) {
      days.push(new Date(current))
    }
    current = addDays(current, 1)
  }
  return days
}

function getRevisionStart(exam: Exam, weekDayRests: number[], specificRests: Date[]): Date | null {
  if (!exam.revisionDays || exam.revisionDays === 0) return null
  let revStart = startOfDay(exam.examDate)
  let counted = 0
  let check = addDays(revStart, -1)
  while (counted < exam.revisionDays) {
    if (!isRestDay(check, weekDayRests, specificRests)) {
      counted++
      revStart = new Date(check)
    }
    check = addDays(check, -1)
  }
  return revStart
}

export function generateMixedPlan(exams: Exam[], weekDayRests: number[], specificRests: Date[]): StudyPlan {
  const today = startOfDay(new Date())
  const activeExams = exams.filter(e => e.name && e.totalPages > 0 && isAfter(startOfDay(e.examDate), today))
  if (activeExams.length === 0) return { days: [], totalDays: 0, avgHoursPerDay: 0, stressLevel: 'low' }

  const lastExamDate = activeExams.reduce((latest, e) =>
    isAfter(e.examDate, latest) ? e.examDate : latest, activeExams[0].examDate)

  const examProgress: Record<string, number> = {}
  activeExams.forEach(e => { examProgress[e.id] = 0 })

  const examRevisionStart: Record<string, Date | null> = {}
  activeExams.forEach(e => { examRevisionStart[e.id] = getRevisionStart(e, weekDayRests, specificRests) })

  const examStudyDays: Record<string, Date[]> = {}
  activeExams.forEach(e => {
    const revStart = examRevisionStart[e.id]
    const studyEnd = revStart || startOfDay(e.examDate)
    examStudyDays[e.id] = getStudyDays(today, studyEnd, weekDayRests, specificRests)
  })

  const examPagesPerDay: Record<string, number> = {}
  activeExams.forEach(e => {
    const days = examStudyDays[e.id].length
    examPagesPerDay[e.id] = days > 0 ? Math.ceil(e.totalPages / days) : e.totalPages
  })

  const dayPlans: DayPlan[] = []
  let current = startOfDay(today)

  while (!isAfter(current, startOfDay(lastExamDate))) {
    const examToday = activeExams.find(e => isSameDay(startOfDay(e.examDate), current))

    if (examToday) {
      dayPlans.push({ date: new Date(current), isRestDay: false, isExamDay: true, examName: examToday.name, examColor: examToday.color, subjects: [] })
      current = addDays(current, 1)
      continue
    }

    if (isRestDay(current, weekDayRests, specificRests)) {
      dayPlans.push({ date: new Date(current), isRestDay: true, isExamDay: false, subjects: [] })
      current = addDays(current, 1)
      continue
    }

    const subjects: SubjectPlan[] = []

    for (const exam of activeExams) {
      if (!isAfter(startOfDay(exam.examDate), current) && !isSameDay(startOfDay(exam.examDate), current)) continue

      const revStart = examRevisionStart[exam.id]
      const isRevision = revStart && (isAfter(current, revStart) || isSameDay(current, revStart))

      if (isRevision) {
        subjects.push({ examId: exam.id, examName: exam.name, color: exam.color, startPage: 0, endPage: 0, pages: 0, minutes: 0, isRevision: true, progressPercent: Math.round((examProgress[exam.id] / exam.totalPages) * 100) })
        continue
      }

      if (examProgress[exam.id] >= exam.totalPages) continue

      const pagesLeft = exam.totalPages - examProgress[exam.id]
      const pages = Math.min(examPagesPerDay[exam.id], pagesLeft)
      const startPage = examProgress[exam.id] + 1
      const endPage = examProgress[exam.id] + pages
      examProgress[exam.id] += pages

      subjects.push({ examId: exam.id, examName: exam.name, color: exam.color, startPage, endPage, pages, minutes: pages * getMinutesPerPage(exam), isRevision: false, progressPercent: Math.round((examProgress[exam.id] / exam.totalPages) * 100) })
    }

    if (subjects.length > 0) {
      dayPlans.push({ date: new Date(current), isRestDay: false, isExamDay: false, subjects })
    }

    current = addDays(current, 1)
  }

  const studyDays = dayPlans.filter(d => !d.isRestDay && !d.isExamDay && d.subjects.length > 0)
  const totalMinutes = studyDays.reduce((sum, d) => sum + d.subjects.reduce((s, sub) => s + sub.minutes, 0), 0)
  const avgMinutes = studyDays.length > 0 ? totalMinutes / studyDays.length : 0
  const avgHours = avgMinutes / 60

  return { days: dayPlans, totalDays: studyDays.length, avgHoursPerDay: avgHours, stressLevel: avgHours <= 2 ? "verylow" : avgHours <= 3 ? "low" : avgHours <= 5 ? "medium" : avgHours <= 7 ? "high" : "veryhigh" }
}

export function generateSeparatePlan(exams: Exam[], weekDayRests: number[], specificRests: Date[]): StudyPlan {
  const today = startOfDay(new Date())
  const activeExams = exams.filter(e => e.name && e.totalPages > 0 && isAfter(startOfDay(e.examDate), today))
  if (activeExams.length === 0) return { days: [], totalDays: 0, avgHoursPerDay: 0, stressLevel: 'low' }

  const lastExamDate = activeExams.reduce((latest, e) =>
    isAfter(e.examDate, latest) ? e.examDate : latest, activeExams[0].examDate)

  const examRevisionStart: Record<string, Date | null> = {}
  activeExams.forEach(e => { examRevisionStart[e.id] = getRevisionStart(e, weekDayRests, specificRests) })

  const examProgress: Record<string, number> = {}
  activeExams.forEach(e => { examProgress[e.id] = 0 })

  const dayPlans: DayPlan[] = []
  let current = startOfDay(today)

  while (!isAfter(current, startOfDay(lastExamDate))) {
    const examToday = activeExams.find(e => isSameDay(startOfDay(e.examDate), current))

    if (examToday) {
      dayPlans.push({ date: new Date(current), isRestDay: false, isExamDay: true, examName: examToday.name, examColor: examToday.color, subjects: [] })
      current = addDays(current, 1)
      continue
    }

    if (isRestDay(current, weekDayRests, specificRests)) {
      dayPlans.push({ date: new Date(current), isRestDay: true, isExamDay: false, subjects: [] })
      current = addDays(current, 1)
      continue
    }

    // Controlla materie in ripasso
    const revisionExams = activeExams.filter(e => {
      const revStart = examRevisionStart[e.id]
      return revStart && (isAfter(current, revStart) || isSameDay(current, revStart)) && isAfter(startOfDay(e.examDate), current)
    })

    if (revisionExams.length > 0) {
      const subjects: SubjectPlan[] = revisionExams.map(e => ({
        examId: e.id, examName: e.name, color: e.color,
        startPage: 0, endPage: 0, pages: 0, minutes: 0, isRevision: true,
        progressPercent: Math.round((examProgress[e.id] / e.totalPages) * 100)
      }))
      dayPlans.push({ date: new Date(current), isRestDay: false, isExamDay: false, subjects })
      current = addDays(current, 1)
      continue
    }

    // Materie ancora da studiare (non in ripasso, non finite)
    const eligible = activeExams.filter(e => {
      if (examProgress[e.id] >= e.totalPages) return false
      const revStart = examRevisionStart[e.id]
      if (revStart && (isAfter(current, revStart) || isSameDay(current, revStart))) return false
      if (!isAfter(startOfDay(e.examDate), current)) return false
      return true
    })

    if (eligible.length === 0) {
      current = addDays(current, 1)
      continue
    }

    // Calcola urgenza: pagine rimanenti / giorni studio disponibili prima della deadline
    const scored = eligible.map(e => {
      const revStart = examRevisionStart[e.id]
      const studyEnd = revStart || startOfDay(e.examDate)
      const studyDaysLeft = getStudyDays(addDays(current, 1), studyEnd, weekDayRests, specificRests).length + 1
      const pagesLeft = e.totalPages - examProgress[e.id]
      const urgency = studyDaysLeft > 0 ? pagesLeft / studyDaysLeft : 9999
      return { exam: e, urgency, studyDaysLeft, pagesLeft }
    })
    scored.sort((a, b) => b.urgency - a.urgency || a.studyDaysLeft - b.studyDaysLeft)
    const { exam: chosen, studyDaysLeft, pagesLeft } = scored[0]

    // Distribuisci le pagine equamente nei giorni rimanenti
    const pagesThisDay = studyDaysLeft > 0 ? Math.ceil(pagesLeft / studyDaysLeft) : pagesLeft
    const startPage = examProgress[chosen.id] + 1
    const endPage = Math.min(examProgress[chosen.id] + pagesThisDay, chosen.totalPages)
    const actualPages = endPage - startPage + 1
    examProgress[chosen.id] += actualPages

    dayPlans.push({
      date: new Date(current), isRestDay: false, isExamDay: false,
      subjects: [{
        examId: chosen.id, examName: chosen.name, color: chosen.color,
        startPage, endPage, pages: actualPages,
        minutes: actualPages * getMinutesPerPage(chosen),
        isRevision: false,
        progressPercent: Math.round((examProgress[chosen.id] / chosen.totalPages) * 100)
      }]
    })

    current = addDays(current, 1)
  }

  const studyDays = dayPlans.filter(d => !d.isRestDay && !d.isExamDay && d.subjects.length > 0)
  const totalMinutes = studyDays.reduce((sum, d) => sum + d.subjects.reduce((s, sub) => s + sub.minutes, 0), 0)
  const avgMinutes = studyDays.length > 0 ? totalMinutes / studyDays.length : 0
  const avgHours = avgMinutes / 60

  return { days: dayPlans, totalDays: studyDays.length, avgHoursPerDay: avgHours, stressLevel: avgHours <= 2 ? "verylow" : avgHours <= 3 ? "low" : avgHours <= 5 ? "medium" : avgHours <= 7 ? "high" : "veryhigh" }
}
export function replanFromProgress(
  plan: StudyPlan,
  wizardState: any,
  dayIndex: number,
  subjectName: string,
  pagesStudied: number
): StudyPlan {
  const { exams } = wizardState

  // Trova l'esame corrispondente
  const exam = exams.find((e: any) => e.name === subjectName)
  if (!exam) return plan

  // Pagine completate = startPage della sessione di oggi - 1 + pagine studiate oggi
  let pagesCompleted = 0
  const dayData = plan.days[dayIndex]
  if (dayData && dayData.subjects) {
    const sub = dayData.subjects.find((s: any) => s.examName === subjectName && s.isRevision === false)
    if (sub) {
      pagesCompleted = (sub.startPage - 1) + pagesStudied
    }
  }

  // Pagine rimanenti per questa materia
  const pagesLeft = exam.totalPages - pagesCompleted
  if (pagesLeft <= 0) return plan

  // Conta giorni futuri con questa materia
  const futureDaysCount = plan.days.slice(dayIndex + 1).filter(
    (d: any) => !d.isRestDay && !d.isExamDay && d.subjects.some((s: any) => s.examName === subjectName && !s.isRevision)
  ).length

  if (futureDaysCount === 0) return plan

  const newPagesPerDay = Math.ceil(pagesLeft / futureDaysCount)

  // Aggiorna anche il giorno corrente con le pagine realmente studiate
  let subjectCursor = pagesCompleted
  const newDays = plan.days.map((day: any, i: number) => {
    if (i < dayIndex) return day
    if (i === dayIndex) {
      const updatedSubjects = day.subjects.map((sub: any) => {
        if (sub.examName !== subjectName || sub.isRevision) return sub
        const endPage = (sub.startPage - 1) + pagesStudied
        return { ...sub, endPage, pages: pagesStudied, progressPercent: Math.round((endPage / exam.totalPages) * 100) }
      })
      return { ...day, subjects: updatedSubjects }
    }
    if (day.isRestDay || day.isExamDay) return day

    const newSubjects = day.subjects.map((sub: any) => {
      // Lascia invariate tutte le altre materie
      if (sub.examName !== subjectName || sub.isRevision) return sub
      
      const startPage = subjectCursor + 1
      const endPage = Math.min(subjectCursor + newPagesPerDay, exam.totalPages)
      const pages = endPage - startPage + 1
      subjectCursor = endPage
      const progressPercent = Math.round((endPage / exam.totalPages) * 100)
      return { ...sub, startPage, endPage, pages, progressPercent }
    })
    return { ...day, subjects: newSubjects }
  })

  return { ...plan, days: newDays }
}
